'use client';

import Link from 'next/link';
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha } from '@mui/material/styles';
import type { ChipOwnProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { blue } from '@/lib/colours';
import { isNullOrUndefined } from '@/lib/comparison';
import { Box, Button, Chip, Menu, MenuItem, Typography } from '@/primitives';
import type { TabDescriptor } from '@/primitives/Tabs/Tabs';

const TAB_GAP_PX = 8;
const FIT_EPSILON = 0.5;

function computeVisibleTabCount(
  tabWidths: number[],
  containerWidth: number,
  moreButtonWidth: number,
): number {
  const n = tabWidths.length;
  if (n === 0) return 0;
  if (containerWidth <= 0) return 1;

  for (let k = n; k >= 1; k--) {
    let sum = 0;
    for (let i = 0; i < k; i++) {
      sum += tabWidths[i]! + (i > 0 ? TAB_GAP_PX : 0);
    }
    if (k === n) {
      if (sum <= containerWidth + FIT_EPSILON) return n;
      continue;
    }
    if (sum + TAB_GAP_PX + moreButtonWidth <= containerWidth + FIT_EPSILON) {
      return k;
    }
  }
  return 1;
}

const linkStyle: React.CSSProperties = {
  textDecoration: 'none',
};

type Props<T extends string> = {
  tabs: Array<TabDescriptor<T>>;
  value: string;
  getHref: (tabValue: T) => string;
  onClick: (tabValue: T) => void;
  tabButtonProps?: ChipOwnProps;
};

export default function SocietySearchHeaderTabRow<T extends string>({
  tabs,
  value,
  getHref,
  onClick,
  tabButtonProps,
}: Props<T>) {
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const measureTabsRef = useRef<HTMLDivElement>(null);
  const measureMoreRef = useRef<HTMLButtonElement>(null);

  const tabsKey = tabs.map((t) => t.value).join('|');

  useEffect(() => {
    setMenuAnchor(null);
  }, [tabsKey]);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measureRow = measureTabsRef.current;
    const moreEl = measureMoreRef.current;
    if (!container || !measureRow || !moreEl || tabs.length === 0) {
      setVisibleCount(tabs.length);
      return;
    }

    const widths = Array.from(measureRow.children).map(
      (child) => (child as HTMLElement).offsetWidth,
    );
    if (widths.length !== tabs.length) {
      setVisibleCount(tabs.length);
      return;
    }

    const moreWidth = moreEl.offsetWidth;
    const containerWidth = container.getBoundingClientRect().width;
    const visibleTabCount = computeVisibleTabCount(
      widths,
      containerWidth,
      moreWidth,
    );
    setVisibleCount(visibleTabCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tabsKey deliberately re-measures when tab *content* changes (not just the count)
  }, [tabs.length, tabsKey]);

  useLayoutEffect(() => {
    recompute();
    const ro = new ResizeObserver(() => {
      recompute();
    });
    const c = containerRef.current;
    const m = measureTabsRef.current;
    if (c) ro.observe(c);
    if (m) ro.observe(m);

    // The first measurement runs before web fonts load, so labels are sized in
    // a wider fallback font and "More" can appear even when the real (narrower)
    // tabs fit. Re-measure once fonts are ready so the count reflects the
    // final text widths.
    let cancelled = false;
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (fonts?.ready) {
      fonts.ready.then(() => {
        if (!cancelled) recompute();
      });
    }

    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [recompute]);

  const visibleTabs = tabs.slice(0, visibleCount);
  const overflowTabs = tabs.slice(visibleCount);
  const overflowSelected = overflowTabs.some((t) => t.value === value);

  const renderChip = (tab: TabDescriptor<T>, isSelected: boolean) => {
    const ddActionName = `Tab: ${String(tab.value)}`;
    return (
      <Chip
        {...tabButtonProps}
        data-dd-action-name={ddActionName}
        disabled={tab.disabled}
        aria-current={isSelected}
        className={isSelected ? 'selectedTab' : ''}
        label={
          <>
            {tab.icon != null && (
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                {tab.icon}
              </Box>
            )}
            <Typography variant="labellarge">{tab.label}</Typography>
            {!isNullOrUndefined(tab.badgeContent) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'action.hover',
                  borderRadius: '28px',
                  height: '18px',
                  minWidth: '18px',
                  padding: '2px',
                }}
              >
                <Typography variant="labelsmall">{tab.badgeContent}</Typography>
              </Box>
            )}
          </>
        }
        component="div"
        sx={(theme) => ({
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '0',
          },
          backgroundColor: isSelected ? blue[100] : 'transparent',
          color: isSelected ? 'primary.main' : 'text.secondary',
          padding: '24px',
          ...(isSelected &&
            theme.applyStyles('dark', {
              backgroundColor: alpha(blue[500], 0.22),
            })),
          ...(tabButtonProps?.sx as Exclude<
            SxProps<Theme>,
            ReadonlyArray<unknown>
          >),
        })}
      />
    );
  };

  const measureRowSx = {
    position: 'absolute',
    left: -99999,
    top: 0,
    display: 'flex',
    flexDirection: 'row' as const,
    gap: `${TAB_GAP_PX}px`,
    flexWrap: 'nowrap' as const,
    visibility: 'hidden' as const,
    pointerEvents: 'none' as const,
    whiteSpace: 'nowrap' as const,
  };

  const moreButtonSx = {
    flexShrink: 0,
    textTransform: 'none' as const,
    fontSize: '12px',
    fontWeight: 600,
    minWidth: 0,
    px: 1.25,
    py: 0.5,
    height: 36,
    borderRadius: '4px',
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <Box ref={measureTabsRef} sx={measureRowSx} aria-hidden>
        {tabs.map((tab, index) => {
          const isSelected = tab.value === value;
          const ddActionName = `Tab: ${String(tab.value)}`;
          const href = getHref(tab.value);
          return (
            <Link
              key={`${String(tab.value)}-${index}`}
              href={href}
              data-dd-action-name={ddActionName}
              style={linkStyle}
              tabIndex={-1}
            >
              {renderChip(tab, isSelected)}
            </Link>
          );
        })}
      </Box>
      <Button
        ref={measureMoreRef}
        size="small"
        variant="outlined"
        aria-hidden
        tabIndex={-1}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        sx={{
          ...moreButtonSx,
          position: 'absolute',
          left: -99999,
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        More
      </Button>

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: `${TAB_GAP_PX}px`,
            flexWrap: 'nowrap',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {visibleTabs.map((tab, index) => {
            const isSelected = tab.value === value;
            const ddActionName = `Tab: ${String(tab.value)}`;
            const href = getHref(tab.value);
            return (
              <Link
                key={`${String(tab.value)}-${index}`}
                href={href}
                data-dd-action-name={ddActionName}
                style={linkStyle}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  if (e.ctrlKey || e.metaKey || e.button !== 0) return;
                  e.preventDefault();
                  onClick(tab.value);
                }}
              >
                {renderChip(tab, isSelected)}
              </Link>
            );
          })}
          {overflowTabs.length > 0 ? (
            <>
              <Button
                size="small"
                variant="outlined"
                aria-haspopup="menu"
                aria-expanded={menuAnchor ? 'true' : 'false'}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                sx={{
                  ...moreButtonSx,
                  ...(overflowSelected && {
                    color: 'primary.main',
                    fontWeight: 700,
                    borderColor: 'primary.main',
                  }),
                }}
                onClick={(e) => {
                  setMenuAnchor(e.currentTarget);
                }}
              >
                More
              </Button>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {overflowTabs.map((tab) => (
                  <MenuItem
                    key={String(tab.value)}
                    selected={tab.value === value}
                    onClick={() => {
                      onClick(tab.value);
                      setMenuAnchor(null);
                    }}
                  >
                    {tab.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
